import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiArrowLeft } from 'react-icons/fi';
import '../users/userform.css';
import '../users/userlist.css';
import '../tasks/tasklist.css';
import './flowdoRole.css';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import BackToTop from '../components/BackToTop/BackToTop';
import CommonTable from '../components/CommonTable/CommonTable';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { flowdoRoleService } from '../../services/flowdoRoleService';
import { formatDateTimeToDDMMMYYYY } from '../../utils/dateFormatter';
import { navigateBackSafely } from '../../utils/navigateBackSafely';
import { applyLinkedSectionView, linkDocumentClassificationView, withClassificationActions } from '../../utils/classificationAccess';

const formatDateTime = (value) => formatDateTimeToDDMMMYYYY(value) || '—';

const ACCESS_MODULE_GROUPS = [
  {
    id: 'tasks',
    title: 'Tasks',
    icon: 'fas fa-tasks',
    keys: ['tasks', 'worksheet', 'performance', 'projects', 'work_register', 'work_register_team'],
  },
  {
    id: 'snap_bill',
    title: 'Snap Bill',
    icon: 'fas fa-file-invoice',
    keys: [
      'invoice_scanner_access',
      'invoice_scanner_invoices',
      'invoice_scanner_quotations',
      'invoice_scanner_soa',
      'invoice_scanner_settings',
      'invoice_scanner_vendors',
      'invoice_scanner_customers',
      'invoice_scanner_projects',
      'invoice_scanner_accounts',
    ],
  },
  {
    id: 'collaboration',
    title: 'Collaboration',
    icon: 'fas fa-comments',
    keys: ['forum', 'documents', 'doc_classifications'],
  },
  {
    id: 'organization',
    title: 'Organization',
    icon: 'fas fa-building',
    keys: ['users', 'staff', 'settings', 'vehicles', 'credentials'],
  },
];

const ACCESS_ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'create_edit', label: 'Create / Edit' },
  { key: 'delete', label: 'Delete' },
  { key: 'export', label: 'Export' },
  { key: 'share', label: 'Share' },
  { key: 'download', label: 'Download' },
];

const sectionDisplayName = (section) =>
  section?.key === 'invoice_scanner_access' ? 'Snap Bill' : section?.name;

const sectionAllowsAction = (section, actionKey) =>
  (section?.actions || []).some((action) => action.key === actionKey);

const MODULE_ICONS = {
  tasks: 'fas fa-tasks',
  worksheet: 'fas fa-table',
  performance: 'fas fa-chart-line',
  projects: 'fas fa-briefcase',
  work_register: 'fas fa-clipboard-list',
  work_register_team: 'fas fa-users',
  forum: 'fas fa-comments',
  documents: 'fas fa-file-alt',
  doc_classifications: 'fas fa-tags',
  users: 'fas fa-users',
  staff: 'fas fa-user-tie',
  settings: 'fas fa-cog',
  vehicles: 'fas fa-car',
  credentials: 'fas fa-key',
  invoice_scanner_access: 'fas fa-external-link-alt',
  invoice_scanner_invoices: 'fas fa-file-invoice',
  invoice_scanner_quotations: 'fas fa-file-signature',
  invoice_scanner_soa: 'fas fa-file-invoice-dollar',
  invoice_scanner_settings: 'fas fa-cog',
  invoice_scanner_vendors: 'fas fa-truck',
  invoice_scanner_customers: 'fas fa-user-friends',
  invoice_scanner_projects: 'fas fa-project-diagram',
  invoice_scanner_accounts: 'fas fa-sitemap',
};

function SelectAllCheckbox({ checked, indeterminate, disabled, onChange, label }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <label className={`flowdo-role-select-all ${checked ? 'is-checked' : ''}`}>
      <input
        ref={inputRef}
        className="form-check-input"
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={label}
      />
      <span>Select all</span>
    </label>
  );
}

const groupCatalogByModule = (catalog) => {
  const used = new Set();
  const groups = ACCESS_MODULE_GROUPS.map((group) => {
    const sections = group.keys
      .map((key) => (catalog || []).find((section) => section.key === key))
      .filter(Boolean);
    sections.forEach((section) => used.add(section.key));
    const actions = ACCESS_ACTIONS.filter((action) =>
      sections.some((section) => sectionAllowsAction(section, action.key))
    );
    return { ...group, sections, actions };
  }).filter((group) => group.sections.length);
  const leftover = (catalog || []).filter((section) => !used.has(section.key));
  if (leftover.length) {
    groups.push({
      id: 'other',
      title: 'Other',
      icon: 'fas fa-th-large',
      sections: leftover,
      actions: ACCESS_ACTIONS.filter((action) =>
        leftover.some((section) => sectionAllowsAction(section, action.key))
      ),
    });
  }
  return groups;
};

const emptySelection = (sections) => {
  const next = {};
  (sections || []).forEach((section) => {
    next[section.key] = {};
    section.actions.forEach((action) => {
      next[section.key][action.key] = false;
    });
  });
  return next;
};

const selectionFromRole = (access) => {
  const next = {};
  (access || []).forEach((section) => {
    next[section.section_key] = {};
    section.actions.forEach((action) => {
      next[section.section_key][action.key] = !!action.allowed;
    });
  });
  return next;
};

const FlowdoRoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [name, setName] = useState('');
  const [status, setStatus] = useState('active');
  const [isSystem, setIsSystem] = useState(false);
  const [createdAt, setCreatedAt] = useState(null);
  const [createdByName, setCreatedByName] = useState(null);
  const [selected, setSelected] = useState({});
  const [users, setUsers] = useState([]);
  const moduleGroups = useMemo(() => groupCatalogByModule(catalog), [catalog]);
  const pageActions = useMemo(() => {
    const used = new Set();
    moduleGroups.forEach((group) => {
      (group.actions || []).forEach((action) => used.add(action.key));
    });
    return ACCESS_ACTIONS.filter((action) => used.has(action.key));
  }, [moduleGroups]);

  const notifySuccess = (message) => toast.success(message, { position: 'top-right', autoClose: 5000 });
  const notifyError = (message) => toast.error(message, { position: 'top-right', autoClose: 5000 });

  const goBack = () => navigateBackSafely(navigate, '/settings/flowdoRoles');

  useEffect(() => {
    const load = async () => {
      try {
        const catalogRes = await flowdoRoleService.getCatalog();
        const sections = withClassificationActions(catalogRes.data?.data?.sections || []);
        setCatalog(sections);
        if (isNew) {
          setSelected(emptySelection(sections));
          setLoading(false);
          return;
        }
        const roleRes = await flowdoRoleService.get(id);
        const role = roleRes.data?.data;
        if (!role) {
          notifyError('Role not found');
          setLoading(false);
          return;
        }
        setName(role.name || '');
        setStatus(role.status || 'active');
        setIsSystem(!!role.is_system);
        setCreatedAt(role.created_at);
        setCreatedByName(role.created_by_name);
        setSelected(linkDocumentClassificationView(selectionFromRole(role.access)));
        setUsers(role.users || []);
      } catch (err) {
        notifyError(err.response?.data?.message || 'Failed to load role');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew]);

  const groupChecks = (group) => {
    let total = 0;
    let checkedCount = 0;
    group.sections.forEach((section) => {
      (section.actions || []).forEach((action) => {
        total += 1;
        if (selected[section.key]?.[action.key]) checkedCount += 1;
      });
    });
    return {
      all: total > 0 && checkedCount === total,
      partial: checkedCount > 0 && checkedCount < total,
    };
  };

  const toggleGroup = (group, checked) => {
    setSelected((prev) => {
      const next = { ...prev };
      group.sections.forEach((section) => {
        const row = { ...(next[section.key] || {}) };
        (section.actions || []).forEach((action) => {
          row[action.key] = checked;
        });
        next[section.key] = row;
      });
      return next;
    });
  };

  const toggleAction = (sectionKey, actionKey, checked) => {
    setSelected((prev) => applyLinkedSectionView(prev, sectionKey, actionKey, checked));
  };

  const buildSections = () => catalog.map((section) => ({
    section_key: section.key,
    actions: section.actions
      .map((action) => action.key)
      .filter((actionKey) => selected[section.key]?.[actionKey]),
  })).filter((section) => section.actions.length > 0);

  const handleSave = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      notifyError('Role name is required');
      return;
    }
    setSaving(true);
    const sections = buildSections();
    try {
      if (isNew) {
        const res = await flowdoRoleService.create({ name: trimmed, status, sections });
        const newId = res.data?.data?.id;
        if (newId && status === 'active') {
          await flowdoRoleService.updateAccess(newId, sections);
        }
        notifySuccess('Role saved');
        navigate('/settings/flowdoRoles', { replace: true });
        return;
      }
      await flowdoRoleService.update(id, { name: trimmed, status, sections });
      if (status === 'active') {
        await flowdoRoleService.updateAccess(id, sections);
        window.dispatchEvent(new Event('menu-access-updated'));
      }
      notifySuccess('Role saved');
    } catch (err) {
      notifyError(err.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const userColumns = [
    { key: 'username', header: 'User Name', accessor: (row) => row.username || '—' },
    { key: 'full_name', header: 'Full Name', accessor: 'full_name' },
    { key: 'branch', header: 'Branch', accessor: (row) => row.branch || '—' },
    { key: 'department', header: 'Department', accessor: (row) => row.department || '—' },
    { key: 'sub_department', header: 'Sub Department', accessor: (row) => row.sub_department || '—' },
    { key: 'assigned', header: 'Role Assigned Date', accessor: (row) => formatDateTime(row.role_assigned_at) },
  ];

  return (
    <div className="flowdo-role-page">
      <button type="button" className="back-button" onClick={goBack}>
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <div className="row" style={{ marginBottom: '0px !important' }}>
        <ToastContainer />
        <div className="col-xl-12">
          <div className="card user-list-card">
            <div className="card-body">
              {loading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <form onSubmit={handleSave}>
                    <div className="row">
                      <div className="col-lg-6 mb-2">
                        <div className="form-group mb-3">
                          <label className="text-label" htmlFor="role-name">Name of the Role*</label>
                          <input
                            id="role-name"
                            type="text"
                            className="form-control"
                            maxLength={50}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Role name"
                            required
                          />
                        </div>
                      </div>
                      <div className="col-lg-6 mb-2">
                        <div className="form-group mb-3">
                          <label className="text-label d-block">Status</label>
                          <div className="flowdo-role-choices mt-2">
                            <label className={`flowdo-role-choice ${status === 'active' ? 'is-selected' : ''}`} htmlFor="role-active">
                              <input
                                className="form-check-input"
                                type="radio"
                                name="roleStatus"
                                id="role-active"
                                checked={status === 'active'}
                                onChange={() => setStatus('active')}
                              />
                              <span className="form-check-label">Active</span>
                            </label>
                            <label
                              className={`flowdo-role-choice ${status === 'inactive' ? 'is-selected' : ''} ${isSystem ? 'is-disabled' : ''}`}
                              htmlFor="role-inactive"
                            >
                              <input
                                className="form-check-input"
                                type="radio"
                                name="roleStatus"
                                id="role-inactive"
                                checked={status === 'inactive'}
                                onChange={() => setStatus('inactive')}
                                disabled={isSystem}
                              />
                              <span className="form-check-label">Inactive</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      {!isNew && (
                        <div className="col-lg-12 mb-3">
                          <p className="flowdo-role-meta">
                            <span>Created {formatDateTime(createdAt)}</span>
                            {createdByName ? <span>by {createdByName}</span> : null}
                          </p>
                        </div>
                      )}
                    </div>

                    <section className="flowdo-role-panel">
                      <header className="flowdo-role-panel__head">
                        <h3>Role Access Control</h3>
                        <p>View is selected automatically when any other option is selected. Documents and Classifications view stay selected together.</p>
                      </header>
                      <div className="flowdo-role-panel__body">
                        <div className={`flowdo-role-access-modules${status === 'inactive' ? ' is-disabled' : ''}`}>
                          {moduleGroups.map((group) => {
                            const selection = groupChecks(group);
                            return (
                            <div className="flowdo-role-module-group" key={group.id}>
                              <h4 className="flowdo-role-module-group__title">
                                <i className={group.icon} aria-hidden="true" />
                                {group.title}
                                <SelectAllCheckbox
                                  checked={selection.all}
                                  indeterminate={selection.partial}
                                  disabled={status === 'inactive'}
                                  label={`Select all ${group.title} permissions`}
                                  onChange={(event) => toggleGroup(group, event.target.checked)}
                                />
                              </h4>
                              <div className="table-responsive">
                                <table className="flowdo-role-access-table">
                                  <thead>
                                    <tr>
                                      <th>Module</th>
                                      {pageActions.map((action) => (
                                        <th key={action.key}>{action.label}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {group.sections.map((section) => (
                                      <tr key={section.key}>
                                        <td>
                                          <span className="flowdo-role-access__name">
                                            <i
                                              className={MODULE_ICONS[section.key] || 'fas fa-cube'}
                                              aria-hidden="true"
                                            />
                                            {sectionDisplayName(section)}
                                          </span>
                                        </td>
                                        {pageActions.map((action) => {
                                          const applicable = sectionAllowsAction(section, action.key);
                                          const checked = applicable && !!selected[section.key]?.[action.key];
                                          const disabled = !applicable || status === 'inactive';
                                          return (
                                            <td key={action.key} className="flowdo-role-access-table__check">
                                              <label className={`flowdo-role-check ${checked ? 'is-checked' : ''} ${!applicable ? 'is-na' : ''}`}>
                                                <input
                                                  className="form-check-input"
                                                  type="checkbox"
                                                  checked={checked}
                                                  disabled={disabled}
                                                  onChange={(e) => {
                                                    if (!applicable) return;
                                                    toggleAction(section.key, action.key, e.target.checked);
                                                  }}
                                                  aria-label={`${sectionDisplayName(section)} ${action.label}`}
                                                />
                                              </label>
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    </section>

                    <div className="text-end toolbar toolbar-bottom p-2 flowdo-role-toolbar">
                      <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={goBack}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="new-user-btn" disabled={saving}>
                        {saving ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : (
                          <i className="fas fa-save me-2"></i>
                        )}
                        Save
                      </button>
                    </div>
                  </form>

                  {!isNew && (
                    <section className="flowdo-role-panel">
                      <header className="flowdo-role-panel__head">
                        <h3>Users</h3>
                        <p>People currently assigned to this role.</p>
                      </header>
                      <div className="flowdo-role-panel__body">
                        <CommonTable
                          columns={userColumns}
                          data={users}
                          emptyMessage="No users are assigned to this role"
                          emptyStateIcon="fas fa-users"
                          getRowId={(row) => row.id}
                          maxHeight="50vh"
                          minHeight="200px"
                        />
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <BackToTop />
      <InternalFooter />
    </div>
  );
};

export default FlowdoRoleForm;
